import * as React from 'react';

import debounce from 'lodash/debounce';
import memoize from 'memoize-one';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { type UnpackICEWP, compose, pure, setDisplayName, withHandlers } from 'recompose';
import * as wicket from 'wicket';

import { deliveryZonesColors, disabledColor } from '../../../constants/deliveryzones.constants';
import { notifyInfo } from '../../../layouts/Notify/actions';
import { createDeliveryZoneByIdSelector } from '../../../selectors/storeDeliveryZone.selector';
import { DeliveryZoneView, IDeliveryZone, IDeliveryZoneMapOptions, IMapShapeProps } from '../types';
import { Circle, MultiPolygon, Polygon } from '.';
import { MAX_POINTS } from './Polygon';

const Wkt = wicket.Wkt;

const parse = (wellKnownText: string) => {
  const wkt = new Wkt();
  wkt.read(wellKnownText);
  return wkt;
};

const createDeliveryZoneViewSelector = () =>
  memoize(
    (
      zone: IDeliveryZone,
      selected: boolean,
      styles,
      zIndex: number,
      disabled
    ): DeliveryZoneView<IDeliveryZoneMapOptions> => {
      return {
        ...zone,
        selected,
        wkt: parse(zone.WellKnownText as string),
        options: {
          ...styles,
          zIndex,
          draggable: false,
          editable: !disabled && selected && (zone.Id as number) > 0,
        },
      };
    }
  );

const onShapeClick = ({ onSelect, zone }: IInjectedProps) => {
  if (zone) {
    onSelect(zone.Id as number);
  }
};
const onShapeChange = ({ onChange, zone }: IInjectedProps, wktString: string) => {
  if (zone !== null) {
    onChange({
      Id: zone.Id,
      DeliveryFee: zone.DeliveryFee,
      MinimumDeliveryOrderAmount: zone.MinimumDeliveryOrderAmount,
      IsEnabled: zone.IsEnabled,
      WellKnownText: wktString,
      FeeChargedToStore: zone.FeeChargedToStore,
    });
  }
};

const handlers = () => {
  const onChangeDebounce = debounce(onShapeChange, 1000);
  return {
    onShapeChange: (props: IInjectedProps) => (wktText: string) => {
      try {
        if (onChangeDebounce) {
          onChangeDebounce.cancel();
        }
        onChangeDebounce(props, wktText);
      } catch (error) {
        console.error(error);
      }
    },
    onShapeClick: (props: IInjectedProps) => () => {
      onShapeClick(props);
    },
  };
};
const useHandlers = withHandlers(handlers);
type Handlers = UnpackICEWP<typeof useHandlers>;

type MapStateToProps = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
type MapDispatchToProps = ReturnType<typeof mapDispatchToProps>;
const mapStateToPropsFactory = (initialState, { storeId, deliveryZoneId }: IMapItemProps) => {
  const zoneSelector = createDeliveryZoneByIdSelector(deliveryZoneId);
  const viewSelector = createDeliveryZoneViewSelector();

  return (state: AppState, { index, disabled }: IMapItemProps) => {
    const zone = zoneSelector(state) as IDeliveryZone;
    const selected = state.deliveryZones.selectedId === zone.Id;
    const zIndex = selected ? 100 : index + 1;
    const count = deliveryZonesColors.length;
    const styles = zone.IsEnabled ? deliveryZonesColors[index % count] : disabledColor;

    const viewZone = viewSelector(zone, selected, styles, zIndex, disabled);
    return {
      translate: getTranslate(state.locale),
      zone: viewZone,
    };
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  notifyPointCapReached: (message: string) =>
    dispatch(
      notifyInfo({
        message,
        translate: false,
      })
    ),
});

export interface IMapItemProps {
  storeId: number;
  deliveryZoneId: number;
  index: number;
  disabled?: boolean;
  onSelect(deliveryZoneId: number);
  onChange(changes: IDeliveryZone);
  onPointCapChange(active: boolean);
}
interface IInjectedProps extends IMapItemProps, MapStateToProps, MapDispatchToProps {}

const MapItem = compose<IInjectedProps & Handlers, IMapItemProps>(
  setDisplayName('MapItem'),
  connect(mapStateToPropsFactory, mapDispatchToProps),
  useHandlers,
  pure
)(({
  zone,
  onShapeClick,
  onShapeChange,
  disabled,
  onPointCapChange,
  translate,
  notifyPointCapReached,
}) => {
  if (!zone) {
    return null;
  }

  const handlePointCapReached = () => {
    // Pretranslate with a manual fallback because the current translation
    // helper can throw for missing keys when placeholder data is involved.
    const translatedMessage = translate('delivery_zone_max_points_reached') as string;
    const messageTemplate =
      typeof translatedMessage === 'string' && translatedMessage !== 'undefined'
        ? translatedMessage
        : `Maximum ${MAX_POINTS} points reached. You can still move existing points.`;

    notifyPointCapReached(messageTemplate.replace(/\$\{0\}/g, String(MAX_POINTS)));
  };

  let MapItemComponent: React.ComponentClass<IMapShapeProps>;
  switch (zone.wkt.type) {
    case 'multipolygon':
      MapItemComponent = MultiPolygon as any;
      break;
    case 'circle':
      MapItemComponent = Circle as any;
      break;
    default:
      MapItemComponent = Polygon as any;
      break;
  }

  return (
    <MapItemComponent
      zone={zone}
      onSelect={onShapeClick}
      onChange={onShapeChange}
      onPointCapChange={onPointCapChange}
      onPointCapReached={handlePointCapReached}
      disabled={disabled}
    />
  );
});

export default MapItem;
